import { useEffect, useState, useCallback } from 'react';
import { regionService, type RegionNodeDto, type RegionFormData } from '../../services/region.service';
import './AdminRegions.css';

const REGION_TYPE_NAMES: Record<number, string> = {
  1: 'Область',
  2: 'Город',
  3: 'Район',
  4: 'Село',
  5: 'Сельский округ',
  6: 'Проезд',
  7: 'Остановка',
  8: 'Улица',
  9: 'Микрорайон',
  10: 'Поселок',
};

const REGION_TYPES = Object.entries(REGION_TYPE_NAMES).map(([v, l]) => ({
  value: Number(v),
  label: l,
}));

const emptyForm: RegionFormData = {
  nameKz: '',
  nameRu: '',
  nameEn: '',
  parentId: null,
  regionType: 1,
};

/** Inline-форма создания/редактирования */
function RegionForm({
  initial,
  parentId,
  onSave,
  onCancel,
  saving,
}: {
  initial?: RegionFormData;
  parentId?: number | null;
  onSave: (data: RegionFormData) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<RegionFormData>(
    initial ?? { ...emptyForm, parentId: parentId ?? null }
  );

  const handleChange = (field: keyof RegionFormData, value: string | number | null) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nameRu.trim() || !form.nameKz.trim() || !form.nameEn.trim()) return;
    onSave(form);
  };

  return (
    <form className="admin-regions-form" onSubmit={handleSubmit}>
      <div className="admin-regions-form-grid">
        <div className="admin-regions-field">
          <label>Название (рус) *</label>
          <input
            type="text"
            value={form.nameRu}
            onChange={(e) => handleChange('nameRu', e.target.value)}
            required
            maxLength={200}
            autoFocus
          />
        </div>
        <div className="admin-regions-field">
          <label>Название (каз) *</label>
          <input
            type="text"
            value={form.nameKz}
            onChange={(e) => handleChange('nameKz', e.target.value)}
            required
            maxLength={200}
          />
        </div>
        <div className="admin-regions-field">
          <label>Название (англ) *</label>
          <input
            type="text"
            value={form.nameEn}
            onChange={(e) => handleChange('nameEn', e.target.value)}
            required
            maxLength={200}
          />
        </div>
        <div className="admin-regions-field">
          <label>Тип региона *</label>
          <select
            value={form.regionType}
            onChange={(e) => handleChange('regionType', Number(e.target.value))}
          >
            {REGION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="admin-regions-form-actions">
        <button type="submit" disabled={saving}>
          {saving ? 'Сохранение...' : initial ? 'Сохранить' : 'Создать'}
        </button>
        <button type="button" className="admin-regions-btn-cancel" onClick={onCancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

/** Узел дерева с lazy-загрузкой детей + кнопки CRUD */
function LazyRegionNode({
  node,
  level = 0,
  onRefreshParent,
}: {
  node: RegionNodeDto;
  level?: number;
  onRefreshParent: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState<RegionNodeDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [childCount, setChildCount] = useState(node.childCount);

  // Add child
  const [showAddChild, setShowAddChild] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  // Edit self
  const [showEdit, setShowEdit] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Delete
  const [deleting, setDeleting] = useState(false);

  // Messages
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const hasChildren = childCount > 0;
  const typeName = REGION_TYPE_NAMES[node.regionType] ?? node.regionTypeName ?? '';

  const loadChildren = useCallback(() => {
    setLoading(true);
    regionService
      .getChildren(node.id)
      .then((res) => {
        if (res.success && res.data) {
          setChildren(res.data);
          setChildCount(res.data.length);
        }
      })
      .finally(() => setLoading(false));
  }, [node.id]);

  const toggle = useCallback(() => {
    if (!open && children === null && hasChildren) {
      loadChildren();
      setOpen(true);
    } else {
      setOpen((o) => !o);
    }
  }, [hasChildren, open, children, loadChildren]);

  const handleAddChild = (data: RegionFormData) => {
    setAddSaving(true);
    setMessage(null);
    regionService
      .create({ ...data, parentId: node.id })
      .then((res) => {
        if (res.success) {
          setMessage({ type: 'ok', text: 'Регион создан' });
          setShowAddChild(false);
          setChildCount((c) => c + 1);
          // Перезагружаем детей
          loadChildren();
          setOpen(true);
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка создания' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка создания' }))
      .finally(() => setAddSaving(false));
  };

  const handleEdit = (data: RegionFormData) => {
    setEditSaving(true);
    setMessage(null);
    regionService
      .update(node.id, data)
      .then((res) => {
        if (res.success) {
          setMessage({ type: 'ok', text: 'Регион обновлён' });
          setShowEdit(false);
          // Перезагружаем родителя, чтобы обновить название
          onRefreshParent();
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка обновления' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка обновления' }))
      .finally(() => setEditSaving(false));
  };

  const handleDelete = () => {
    if (!confirm(`Удалить регион "${node.nameRu}"?`)) return;
    setDeleting(true);
    setMessage(null);
    regionService
      .delete(node.id)
      .then((res) => {
        if (res.success) {
          onRefreshParent();
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка удаления' });
          setDeleting(false);
        }
      })
      .catch(() => {
        setMessage({ type: 'err', text: 'Ошибка удаления' });
        setDeleting(false);
      });
  };

  if (deleting) return null;

  const editFormData: RegionFormData = {
    nameKz: node.nameKz,
    nameRu: node.nameRu,
    nameEn: node.nameEn,
    parentId: node.parentId,
    regionType: node.regionType,
  };

  return (
    <div className="admin-regions-node" style={{ marginLeft: level * 16 }}>
      <div className="admin-regions-row">
        <span
          className={`admin-regions-toggle ${hasChildren ? 'admin-regions-toggle--clickable' : ''}`}
          onClick={toggle}
        >
          {hasChildren ? (loading ? '...' : open ? '▼' : '▶') : ''}
        </span>
        <span className="admin-regions-name" onClick={toggle}>
          {node.nameRu}
        </span>
        <span className="admin-regions-type">{typeName}</span>
        {hasChildren && <span className="admin-regions-count">{childCount}</span>}
        <span className="admin-regions-actions">
          <button
            className="admin-regions-btn admin-regions-btn--add"
            title="Добавить дочерний"
            onClick={(e) => { e.stopPropagation(); setShowAddChild((s) => !s); setShowEdit(false); }}
          >
            +
          </button>
          <button
            className="admin-regions-btn admin-regions-btn--edit"
            title="Редактировать"
            onClick={(e) => { e.stopPropagation(); setShowEdit((s) => !s); setShowAddChild(false); }}
          >
            ✎
          </button>
          {!hasChildren && (
            <button
              className="admin-regions-btn admin-regions-btn--del"
              title="Удалить"
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
            >
              ✕
            </button>
          )}
        </span>
      </div>

      {message && (
        <p className={`admin-regions-msg ${message.type === 'ok' ? 'admin-regions-msg--ok' : 'admin-regions-msg--err'}`}>
          {message.text}
        </p>
      )}

      {showEdit && (
        <RegionForm
          initial={editFormData}
          onSave={handleEdit}
          onCancel={() => setShowEdit(false)}
          saving={editSaving}
        />
      )}

      {showAddChild && (
        <RegionForm
          parentId={node.id}
          onSave={handleAddChild}
          onCancel={() => setShowAddChild(false)}
          saving={addSaving}
        />
      )}

      {open && children && (
        <div className="admin-regions-children">
          {children.map((child) => (
            <LazyRegionNode
              key={child.id}
              node={child}
              level={level + 1}
              onRefreshParent={loadChildren}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Результат поиска + кнопка редактирования */
function SearchResult({
  node,
  onEdited,
}: {
  node: RegionNodeDto;
  onEdited: () => void;
}) {
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const typeName = REGION_TYPE_NAMES[node.regionType] ?? node.regionTypeName ?? '';

  const handleSave = (data: RegionFormData) => {
    setSaving(true);
    setMessage(null);
    regionService
      .update(node.id, data)
      .then((res) => {
        if (res.success) {
          setShowEdit(false);
          onEdited();
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка обновления' }))
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <div className="admin-regions-row admin-regions-search-item">
        <span className="admin-regions-toggle" />
        <span className="admin-regions-name">{node.nameRu}</span>
        <span className="admin-regions-type">{typeName}</span>
        <span className="admin-regions-actions">
          <button
            className="admin-regions-btn admin-regions-btn--edit"
            onClick={() => setShowEdit((s) => !s)}
          >
            ✎
          </button>
        </span>
      </div>
      {message && (
        <p className={`admin-regions-msg ${message.type === 'ok' ? 'admin-regions-msg--ok' : 'admin-regions-msg--err'}`}>
          {message.text}
        </p>
      )}
      {showEdit && (
        <RegionForm
          initial={{
            nameKz: node.nameKz,
            nameRu: node.nameRu,
            nameEn: node.nameEn,
            parentId: node.parentId,
            regionType: node.regionType,
          }}
          onSave={handleSave}
          onCancel={() => setShowEdit(false)}
          saving={saving}
        />
      )}
    </div>
  );
}

export default function AdminRegions() {
  const [roots, setRoots] = useState<RegionNodeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Поиск
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RegionNodeDto[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Добавление корневого
  const [showAddRoot, setShowAddRoot] = useState(false);
  const [addRootSaving, setAddRootSaving] = useState(false);
  const [rootMessage, setRootMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const loadRoots = useCallback(() => {
    setLoading(true);
    regionService
      .getChildren()
      .then((res) => {
        if (res.success && res.data) setRoots(res.data);
        else setError(res.message || 'Ошибка загрузки');
      })
      .catch(() => setError('Ошибка загрузки регионов'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRoots();
  }, [loadRoots]);

  // Debounced поиск
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      regionService
        .search(searchQuery)
        .then((res) => {
          if (res.success && res.data) setSearchResults(res.data);
        })
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleAddRoot = (data: RegionFormData) => {
    setAddRootSaving(true);
    setRootMessage(null);
    regionService
      .create({ ...data, parentId: null })
      .then((res) => {
        if (res.success) {
          setRootMessage({ type: 'ok', text: 'Корневой регион создан' });
          setShowAddRoot(false);
          loadRoots();
        } else {
          setRootMessage({ type: 'err', text: res.message || 'Ошибка создания' });
        }
      })
      .catch(() => setRootMessage({ type: 'err', text: 'Ошибка создания' }))
      .finally(() => setAddRootSaving(false));
  };

  const rerunSearch = () => {
    if (searchQuery.length >= 2) {
      setSearching(true);
      regionService
        .search(searchQuery)
        .then((res) => {
          if (res.success && res.data) setSearchResults(res.data);
        })
        .finally(() => setSearching(false));
    }
  };

  if (loading) {
    return (
      <div className="admin-regions">
        <h1>Регионы</h1>
        <p className="admin-regions-loading">Загрузка...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-regions">
        <h1>Регионы</h1>
        <p className="admin-regions-error">{error}</p>
      </div>
    );
  }

  const isSearching = searchQuery.length >= 2;

  return (
    <div className="admin-regions">
      <div className="admin-regions-header">
        <div>
          <h1>Регионы</h1>
          <p className="admin-regions-desc">
            Иерархия регионов — ленивая загрузка по клику
          </p>
        </div>
        <button
          className="admin-regions-add-root-btn"
          onClick={() => setShowAddRoot((s) => !s)}
        >
          {showAddRoot ? 'Скрыть форму' : 'Добавить корневой регион'}
        </button>
      </div>

      {rootMessage && (
        <p className={`admin-regions-msg ${rootMessage.type === 'ok' ? 'admin-regions-msg--ok' : 'admin-regions-msg--err'}`}>
          {rootMessage.text}
        </p>
      )}

      {showAddRoot && (
        <RegionForm
          onSave={handleAddRoot}
          onCancel={() => setShowAddRoot(false)}
          saving={addRootSaving}
        />
      )}

      <div className="admin-regions-search-bar">
        <input
          type="text"
          placeholder="Поиск по названию (мин. 2 символа)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searching && <span className="admin-regions-search-spinner">...</span>}
      </div>

      {isSearching ? (
        <div className="admin-regions-tree">
          {searchResults && searchResults.length > 0 ? (
            searchResults.map((node) => (
              <SearchResult key={node.id} node={node} onEdited={rerunSearch} />
            ))
          ) : (
            !searching && <p className="admin-regions-empty">Ничего не найдено</p>
          )}
        </div>
      ) : (
        <div className="admin-regions-tree">
          {roots.map((r) => (
            <LazyRegionNode key={r.id} node={r} onRefreshParent={loadRoots} />
          ))}
        </div>
      )}
    </div>
  );
}
